import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Brand, Neutrals, primaryAlpha } from '@/constants/theme';
import { useGroceryList } from '@/hooks/useGroceryList';
import {
  useAddListStaple,
  useDeleteListStaple,
  useListStaples,
} from '@/hooks/useListStaples';
import type { ListStaple } from '@/lib/list-staples-cache';
import { Ionicons } from '@expo/vector-icons';
import { useFocusEffect } from '@react-navigation/native';
import { LinearGradient } from 'expo-linear-gradient';
import { useRouter, useLocalSearchParams } from 'expo-router';
import React, { useCallback, useState } from 'react';
import {
  ActivityIndicator,
  Alert,
  Modal,
  Platform,
  ScrollView,
  StyleSheet,
  Text,
  TouchableOpacity,
  View,
} from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

const CATEGORIES = ['Produce', 'Dairy', 'Meat', 'Bakery', 'Pantry', 'Frozen', 'Other'];

export default function ManageStaples() {
  const router = useRouter();
  const insets = useSafeAreaInsets();
  const { listId } = useLocalSearchParams<{ listId?: string }>();
  const listIdOrNull = listId ?? null;
  const { list } = useGroceryList(listIdOrNull ?? undefined);
  const { listStaples, loading, refetch } = useListStaples(listIdOrNull);
  const { addListStaple, loading: addLoading } = useAddListStaple(listIdOrNull);
  const { deleteListStaple, loading: deleteLoading } = useDeleteListStaple(listIdOrNull);
  const [newItemName, setNewItemName] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('Pantry');
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [stapleToDelete, setStapleToDelete] = useState<ListStaple | null>(null);

  const listName = list?.name ?? 'this list';

  useFocusEffect(
    useCallback(() => {
      if (listIdOrNull) refetch();
    }, [listIdOrNull, refetch])
  );

  const handleAddStaple = async () => {
    const trimmed = newItemName.trim();
    if (!trimmed || !listIdOrNull) return;
    try {
      const created = await addListStaple(trimmed, selectedCategory);
      if (created) {
        setNewItemName('');
        refetch(true).catch(() => {});
      }
    } catch {
      Alert.alert('Error', 'Failed to add staple. It may already exist.');
    }
  };

  const handleDeletePress = (staple: ListStaple) => {
    setStapleToDelete(staple);
    setShowDeleteModal(true);
  };

  const handleConfirmDelete = async () => {
    if (!stapleToDelete) return;
    try {
      await deleteListStaple(stapleToDelete.id);
      setShowDeleteModal(false);
      setStapleToDelete(null);
      refetch(true).catch(() => {});
    } catch {
      Alert.alert('Error', 'Failed to remove staple.');
    }
  };

  const goBack = () => router.replace('/(tabs)/grocery-lists');

  if (!listIdOrNull) {
    return (
      <View style={styles.container}>
        <LinearGradient colors={[...Brand.primaryGradient]} style={styles.background} />
        <View style={[styles.scrollContent, { paddingTop: 16 + insets.top, paddingHorizontal: 16 }]}>
          <TouchableOpacity onPress={goBack} style={styles.backButton} hitSlop={{ top: 12, bottom: 12, left: 12, right: 12 }}>
            <Ionicons name="arrow-back" size={20} color={Brand.primary} />
            <Text style={styles.backText}>Back</Text>
          </TouchableOpacity>
          <Text style={styles.headerTitle}>Manage staples</Text>
          <Text style={styles.emptySubtext}>Select a list to manage its quick-add staples.</Text>
        </View>
      </View>
    );
  }

  return (
    <View style={styles.container}>
      <LinearGradient colors={[...Brand.primaryGradient]} style={styles.background} />
      <ScrollView
        style={styles.scrollView}
        contentContainerStyle={[styles.scrollContent, { paddingTop: 16 + insets.top }]}
        keyboardShouldPersistTaps="handled"
      >
        <View style={styles.header}>
          <TouchableOpacity onPress={goBack} style={styles.backButton} hitSlop={{ top: 12, bottom: 12, left: 12, right: 12 }}>
            <Ionicons name="arrow-back" size={20} color={Brand.primary} />
            <Text style={styles.backText}>Back</Text>
          </TouchableOpacity>
          <Text style={styles.headerTitle}>My staples for {listName}</Text>
        </View>

        <Text style={styles.sectionTitle}>Add new staple</Text>
        <View style={styles.addRow}>
          <Input
            value={newItemName}
            onChangeText={setNewItemName}
            placeholder="e.g., Tomatoes"
            style={styles.addInput}
            onSubmitEditing={handleAddStaple}
          />
          <ScrollView horizontal showsHorizontalScrollIndicator={false} contentContainerStyle={styles.categoryChips}>
            {CATEGORIES.map((cat) => (
              <TouchableOpacity
                key={cat}
                onPress={() => setSelectedCategory(cat)}
                style={[styles.categoryChip, selectedCategory === cat && styles.categoryChipActive]}
              >
                <Text style={[styles.categoryChipText, selectedCategory === cat && styles.categoryChipTextActive]}>{cat}</Text>
              </TouchableOpacity>
            ))}
          </ScrollView>
          <Button onPress={handleAddStaple} loading={addLoading} disabled={addLoading || !newItemName.trim()}>
            Add
          </Button>
        </View>

        <Text style={styles.sectionTitle}>Your staples for this list</Text>
        {loading ? (
          <View style={styles.loadingContainer}>
            <ActivityIndicator size="large" color={Brand.primary} />
          </View>
        ) : listStaples.length === 0 ? (
          <View style={styles.emptyContainer}>
            <Ionicons name="pricetag-outline" size={48} color={Neutrals.textMuted} />
            <Text style={styles.emptyText}>No staples yet</Text>
            <Text style={styles.emptySubtext}>Add items for quick add when using this list</Text>
          </View>
        ) : (
          <View style={styles.staplesList}>
            {listStaples.map((staple) => (
              <View key={staple.id} style={styles.stapleRow}>
                <View style={styles.stapleInfo}>
                  <Text style={styles.stapleName}>{staple.item_name}</Text>
                  <Text style={styles.stapleCategory}>{staple.category}</Text>
                </View>
                <TouchableOpacity
                  onPress={() => handleDeletePress(staple)}
                  style={styles.deleteStapleButton}
                  hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
                >
                  <Ionicons name="trash-outline" size={20} color={Brand.destructive} />
                </TouchableOpacity>
              </View>
            ))}
          </View>
        )}
      </ScrollView>

      <Modal
        visible={showDeleteModal}
        transparent
        animationType="fade"
        onRequestClose={() => !deleteLoading && setShowDeleteModal(false)}
      >
        <View style={styles.modalOverlay}>
          <View style={styles.modalContent}>
            <Text style={styles.modalTitle}>Remove staple</Text>
            <Text style={styles.modalDescription}>
              {`Remove "${stapleToDelete?.item_name ?? ''}" from your staples for this list? You can add it back anytime.`}
            </Text>
            <View style={styles.modalActions}>
              <Button
                variant="outline"
                onPress={() => setShowDeleteModal(false)}
                disabled={deleteLoading}
                style={styles.modalButton}
              >
                Cancel
              </Button>
              <Button
                variant="destructive"
                onPress={handleConfirmDelete}
                loading={deleteLoading}
                disabled={deleteLoading}
                style={styles.modalButton}
              >
                Remove
              </Button>
            </View>
          </View>
        </View>
      </Modal>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  background: {
    position: 'absolute',
    left: 0,
    right: 0,
    top: 0,
    bottom: 0,
  },
  header: {
    marginBottom: 24,
  },
  backButton: {
    flexDirection: 'row',
    alignItems: 'center',
    alignSelf: 'flex-start',
    gap: 6,
    marginBottom: 12,
    paddingVertical: 4,
  },
  backText: {
    color: Brand.primary,
    fontSize: 16,
    fontWeight: '500',
    lineHeight: 20,
  },
  headerTitle: {
    fontSize: 28,
    fontWeight: 'bold',
    color: Neutrals.textPrimary,
  },
  scrollView: {
    flex: 1,
  },
  scrollContent: {
    paddingHorizontal: 16,
    paddingBottom: 100,
    maxWidth: 864,
    alignSelf: 'center',
    width: '100%',
  },
  sectionTitle: {
    fontSize: 16,
    fontWeight: '600',
    color: Neutrals.textPrimary,
    marginBottom: 12,
    marginTop: 8,
  },
  addRow: {
    gap: 12,
  },
  addInput: {
    marginBottom: 0,
  },
  categoryChips: {
    flexDirection: 'row',
    gap: 8,
    marginBottom: 8,
  },
  categoryChip: {
    paddingHorizontal: 14,
    paddingVertical: 8,
    borderRadius: 999,
    backgroundColor: Neutrals.whiteFaded90,
    borderWidth: 2,
    borderColor: primaryAlpha(0.3),
  },
  categoryChipActive: {
    backgroundColor: Brand.primary,
    borderColor: Brand.primary,
  },
  categoryChipText: {
    fontSize: 13,
    fontWeight: '600',
    color: Neutrals.textPrimary,
  },
  categoryChipTextActive: {
    color: '#fff',
  },
  loadingContainer: {
    paddingVertical: 48,
    alignItems: 'center',
  },
  emptyContainer: {
    alignItems: 'center',
    paddingVertical: 48,
    paddingHorizontal: 24,
  },
  emptyText: {
    fontSize: 18,
    fontWeight: '600',
    color: Neutrals.textPrimary,
    marginTop: 16,
  },
  emptySubtext: {
    fontSize: 14,
    color: Neutrals.textSecondary,
    marginTop: 8,
    textAlign: 'center',
  },
  staplesList: {
    gap: 8,
  },
  stapleRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    backgroundColor: Neutrals.whiteFaded90,
    borderRadius: 16,
    padding: 16,
    borderWidth: 1,
    borderColor: primaryAlpha(0.15),
  },
  stapleInfo: {
    flex: 1,
  },
  stapleName: {
    fontSize: 16,
    fontWeight: '600',
    color: Neutrals.textPrimary,
  },
  stapleCategory: {
    fontSize: 13,
    color: Neutrals.textSecondary,
    marginTop: 2,
  },
  deleteStapleButton: {
    padding: 8,
  },
  modalOverlay: {
    flex: 1,
    backgroundColor: Neutrals.overlay,
    justifyContent: 'center',
    alignItems: 'center',
    padding: 20,
  },
  modalContent: {
    backgroundColor: '#fff',
    borderRadius: 24,
    padding: 24,
    width: '100%',
    maxWidth: 400,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.3,
    shadowRadius: 8,
    elevation: Platform.OS === 'android' ? 0 : 10,
  },
  modalTitle: {
    fontSize: 24,
    fontWeight: 'bold',
    color: Neutrals.textPrimary,
    marginBottom: 12,
    textAlign: 'center',
  },
  modalDescription: {
    fontSize: 16,
    color: Neutrals.textSecondary,
    marginBottom: 24,
    textAlign: 'center',
    lineHeight: 22,
  },
  modalActions: {
    flexDirection: 'row',
    gap: 12,
  },
  modalButton: {
    flex: 1,
  },
});
